"use client";

import { CheckCheck, Tag, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from "~/components/ui/combobox";
import { apiClient } from "~/lib/api-client";
import type { PromptTag } from "~/types/PromptTypes";

interface TagFilterSelectorProps {
  selectedTagIds: number[];
  onTagsChange: (tagIds: number[]) => void;
  disabled?: boolean;
  className?: string;
}

function sameTagIds(a: number[], b: number[]) {
  if (a.length !== b.length) return false;
  const ids = new Set(a);
  return b.every((id) => ids.has(id));
}

export default function TagFilterSelector({
  selectedTagIds,
  onTagsChange,
  disabled = false,
  className = "",
}: TagFilterSelectorProps) {
  const [tags, setTags] = useState<PromptTag[]>([]);
  const [allTags, setAllTags] = useState<PromptTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [hasCleanedUp, setHasCleanedUp] = useState(false);
  const [draftTagIds, setDraftTagIds] = useState(selectedTagIds);
  const draftTagIdsRef = useRef(selectedTagIds);

  const updateDraft = (ids: number[]) => {
    draftTagIdsRef.current = ids;
    setDraftTagIds(ids);
  };

  useEffect(() => {
    const fetchTags = async () => {
      if (disabled) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get("/prompt-tags");
        const fetchedTags = response.data.data || [];
        setAllTags(fetchedTags);
        const tagsWithPrompts = fetchedTags.filter(
          (tag: PromptTag) => (tag.prompt_count ?? 0) > 0
        );
        setTags(tagsWithPrompts);
      } catch (error) {
        console.error("Failed to fetch tags:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [disabled]);

  useEffect(() => {
    if (open && !disabled) {
      const fetchTags = async () => {
        try {
          const response = await apiClient.get("/prompt-tags");
          const fetchedTags = response.data.data || [];
          setAllTags(fetchedTags);
          const tagsWithPrompts = fetchedTags.filter(
            (tag: PromptTag) => (tag.prompt_count ?? 0) > 0
          );
          setTags(tagsWithPrompts);
        } catch (error) {
          console.error("Failed to fetch tags:", error);
        }
      };
      fetchTags();
    }
  }, [open, disabled]);

  useEffect(() => {
    if (allTags.length > 0 && selectedTagIds.length > 0 && !hasCleanedUp) {
      const timeoutId = setTimeout(() => {
        const validTagIds = selectedTagIds.filter((tagId) => {
          const tag = allTags.find((t) => t.id === tagId);
          return tag && (tag.prompt_count ?? 0) > 0;
        });
        if (validTagIds.length !== selectedTagIds.length) {
          onTagsChange(validTagIds);
        }
        setHasCleanedUp(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [allTags, hasCleanedUp, onTagsChange, selectedTagIds]);

  const selectedItems = tags.filter((tag) => draftTagIds.includes(tag.id));

  return (
    <Combobox
      multiple
      autoHighlight
      items={tags}
      value={selectedItems}
      onValueChange={(next) => updateDraft(next.map((tag) => tag.id))}
      itemToStringLabel={(tag) => tag.name}
      itemToStringValue={(tag) => String(tag.id)}
      isItemEqualToValue={(a, b) => a.id === b.id}
      open={open && !disabled}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          updateDraft(selectedTagIds);
          setOpen(true);
          return;
        }

        setOpen(false);
        const nextIds = draftTagIdsRef.current;
        if (!sameTagIds(nextIds, selectedTagIds)) {
          onTagsChange(nextIds);
        }
      }}
      disabled={disabled}
    >
      <ComboboxTrigger
        render={
          <Button
            variant="outline"
            className={className}
            title={
              disabled
                ? "Tag filtering is not available for single prompt analytics"
                : undefined
            }
          />
        }
      >
        <Tag data-icon="inline-start" />
        {selectedTagIds.length > 0
          ? `${selectedTagIds.length} tag${selectedTagIds.length === 1 ? "" : "s"}`
          : "Tags"}
      </ComboboxTrigger>
      <ComboboxContent
        align="start"
        side="bottom"
        className="w-80 min-w-80 max-w-80"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
          <div className="flex min-w-0 shrink-0 items-center gap-2">
            <Tag className="size-4 shrink-0 text-muted-foreground" />
            <span className="whitespace-nowrap text-sm font-medium">
              Tags Filter
            </span>
            {draftTagIds.length > 0 && (
              <span className="inline-flex shrink-0 items-center justify-center rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
                {draftTagIds.length}/{tags.length}
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => updateDraft(tags.map((t) => t.id))}
              disabled={tags.length === 0}
            >
              <CheckCheck data-icon="inline-start" />
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => updateDraft([])}
              disabled={draftTagIds.length === 0}
            >
              <X data-icon="inline-start" />
              Clear
            </Button>
          </div>
        </div>
        <ComboboxInput
          showTrigger={false}
          placeholder="Search tags..."
          className="w-auto"
        />
        <ComboboxEmpty>
          {loading ? "Loading tags..." : "No tags found."}
        </ComboboxEmpty>
        <ComboboxList className="h-64 overflow-y-auto">
          {(tag: PromptTag) => (
            <ComboboxItem key={tag.id} value={tag}>
              <span
                className="size-4 shrink-0 rounded"
                style={{ backgroundColor: tag.color || "#6B7280" }}
              />
              <span className="flex-1 truncate">{tag.name}</span>
              {tag.prompt_count !== undefined && (
                <span className="text-xs text-muted-foreground">
                  ({tag.prompt_count})
                </span>
              )}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
